import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { recipeCostAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { TrendingUp, TrendingDown, Minus, Loader, CalendarDays, AlertTriangle, CheckCircle, RefreshCw, ChevronDown, ChevronRight, IndianRupee, BookOpen, UtensilsCrossed, ArrowRight } from "lucide-react";

const STATUS_CONFIG = {
  on_target:     { label: "On Target",       color: "text-[#637C64]",  bg: "bg-[#EDF2EE]",  icon: CheckCircle,   border: "border-[#637C64]/30" },
  slightly_over: { label: "Slightly Over",   color: "text-[#D98032]",  bg: "bg-[#FDF4EB]",  icon: TrendingUp,    border: "border-[#D98032]/30" },
  over_budget:   { label: "Over Budget",     color: "text-[#C2624E]",  bg: "bg-[#FBEAE7]",  icon: AlertTriangle, border: "border-[#C2624E]/30" },
  no_target:     { label: "No Target Set",   color: "text-[#8A7E73]",  bg: "bg-[#F4F3F0]",  icon: Minus,         border: "border-[#E5E1DB]" },
};

function CostCard({ item, expanded, onToggle }) {
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.no_target;
  const Icon = cfg.icon;
  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${cfg.border}`}>
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#FAFAF8]" onClick={onToggle}>
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon size={18} className={cfg.color} />
          </div>
          <div>
            <p className="font-bold text-[#3A322C]">{item.menu_item_name}</p>
            <p className="text-xs text-[#8A7E73] capitalize">{item.category} · {item.servings?.toLocaleString()} servings</p>
          </div>
        </div>
        <div className="flex items-center gap-6 text-right">
          <div>
            <p className="text-lg font-extrabold font-[Manrope] text-[#3A322C]">₹{item.cost_per_serving}</p>
            <p className="text-xs text-[#8A7E73]">per serving</p>
          </div>
          {item.target_rate > 0 && (
            <div>
              <p className={`text-sm font-bold ${item.variance > 0 ? "text-[#C2624E]" : "text-[#637C64]"}`}>
                {item.variance > 0 ? "+" : ""}{item.variance} ({item.variance_pct > 0 ? "+" : ""}{item.variance_pct}%)
              </p>
              <p className="text-xs text-[#8A7E73]">vs ₹{item.target_rate} target</p>
            </div>
          )}
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
          {expanded ? <ChevronDown size={16} className="text-[#8A7E73]" /> : <ChevronRight size={16} className="text-[#8A7E73]" />}
        </div>
      </div>
      {expanded && item.ingredients?.length > 0 && (
        <div className="border-t border-[#F4F3F0] px-5 py-4">
          {item.missing_rates && (
            <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-lg px-4 py-2.5 text-xs text-[#D98032] mb-3 flex items-center gap-2">
              <AlertTriangle size={13} /> {item.items_without_rate} ingredient(s) have no rate — cost may be understated. Enter stock inward or vendor rate contracts.
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="bg-[#F4F3F0]">
              <tr>{["Ingredient", "Per Serving", "Total Qty", "Rate (₹)", "Cost (₹)"].map(h => (
                <th key={h} className="text-left px-3 py-2 font-bold uppercase text-[#5C5147]">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-[#F4F3F0]">
              {item.ingredients.map((ing, i) => (
                <tr key={`idx-${i}`} className={`${!ing.has_rate ? "bg-[#FDF4EB]/30" : "hover:bg-[#FAFAF8]"}`}>
                  <td className="px-3 py-2 font-medium text-[#3A322C]">{ing.item_name}</td>
                  <td className="px-3 py-2 text-[#8A7E73]">{ing.qty_per_serving} {ing.unit}</td>
                  <td className="px-3 py-2 text-[#5C5147]">{ing.total_qty} {ing.unit}</td>
                  <td className="px-3 py-2">
                    {ing.has_rate ? <span className="font-bold text-[#637C64]">₹{ing.rate_per_unit}</span>
                      : <span className="text-[#D98032] font-bold">No rate</span>}
                  </td>
                  <td className="px-3 py-2 font-bold text-[#3A322C]">₹{ing.total_cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function RecipeCostCalculator() {
  const navigate = useNavigate();
  const { selectedKitchen } = useKitchen();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (selectedKitchen) calculate();
  }, [date, selectedKitchen]);

  const calculate = async () => {
    setLoading(true); setError("");
    try {
      const r = await recipeCostAPI.calculate({ date, kitchen_id: selectedKitchen || null });
      setResult(r.data);
      if (r.data?.menu_items?.length > 0) setExpanded(r.data.menu_items[0].menu_item_id);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  const summary = result?.summary;
  const items = result?.menu_items || [];
  const overBudget = items.filter(i => i.status === "over_budget");

  return (
    <div className="space-y-6 max-w-5xl" data-testid="recipe-cost">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Recipe Cost Calculator</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Real-time cost per serving based on actual ingredient purchase rates</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
          <button onClick={calculate} disabled={loading}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1 disabled:opacity-60">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Recalculate
          </button>
        </div>
      </div>

      {/* Quick links to Menu & Recipes sub-pages */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Menu Items", sub: "Add dishes to the menu", icon: UtensilsCrossed, path: "/menu?tab=items", color: "text-[#E8956D] bg-[#E8956D]/10" },
          { label: "Recipes",    sub: "Define ingredients & quantities", icon: BookOpen,   path: "/menu?tab=recipes", color: "text-purple-600 bg-purple-50" },
          { label: "Daily Plan", sub: "Plan today's breakfast & lunch", icon: CalendarDays, path: "/menu?tab=plan", color: "text-[#637C64] bg-[#EDF2EE]" },
          { label: "Stock Calculator", sub: "Ingredients needed for plan", icon: TrendingUp, path: "/menu?tab=calculator", color: "text-blue-600 bg-blue-50" },
        ].map(item => (
          <button key={item.label} onClick={() => navigate(item.path)}
            className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-left hover:shadow-md transition-all group">
            <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center mb-3`}>
              <item.icon size={18} />
            </div>
            <p className="font-bold text-[#3A322C] text-sm">{item.label}</p>
            <p className="text-xs text-[#8A7E73] mt-0.5">{item.sub}</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-[#E8956D] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              Open <ArrowRight size={11} />
            </div>
          </button>
        ))}
      </div>

      {error && <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-4 py-3 text-sm text-[#C2624E]">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-[#8A7E73]">
          <Loader size={24} className="animate-spin text-[#E8956D]" />
          <span>Calculating costs from ingredient rates...</span>
        </div>
      ) : result && (
        <div className="space-y-5">
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Total Meal Cost", value: `₹${summary.total_cost?.toLocaleString("en-IN", {maximumFractionDigits:0})}`, sub: "for the day", color: "text-[#E8956D]" },
                { label: "Total Servings", value: summary.total_servings?.toLocaleString(), sub: "children fed", color: "text-[#5C5147]" },
                { label: "Avg Cost/Serving", value: `₹${summary.avg_cost_per_serving}`, sub: "across all meals", color: "text-[#5C5147]" },
                { label: "Over Budget", value: summary.items_over_budget, sub: `of ${items.length} items`, color: summary.items_over_budget > 0 ? "text-[#C2624E]" : "text-[#637C64]" },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className={`text-2xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
                  <p className="text-xs font-bold text-[#3A322C] mt-1">{s.label}</p>
                  <p className="text-xs text-[#8A7E73]">{s.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* Over budget alert */}
          {overBudget.length > 0 && (
            <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-5 py-4 flex items-start gap-3">
              <AlertTriangle size={20} className="text-[#C2624E] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[#C2624E]">{overBudget.length} menu item(s) are over budget</p>
                <p className="text-sm text-[#C2624E]/80 mt-0.5">
                  {overBudget.map(i => `${i.menu_item_name} (+${i.variance_pct}%)`).join(", ")}
                </p>
              </div>
            </div>
          )}

          {/* No plan */}
          {!result.has_plan && (
            <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-5 py-4 text-sm text-[#D98032]">
              No menu plan found for {date}. <a href="/daily-entry" className="font-bold underline">Create a daily plan</a> first to calculate costs.
            </div>
          )}

          {/* Menu item cards */}
          {items.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-bold text-[#3A322C]">Cost Breakdown by Menu Item</h3>
              {items.map(item => (
                <CostCard
                  key={item.menu_item_id}
                  item={item}
                  expanded={expanded === item.menu_item_id}
                  onToggle={() => setExpanded(expanded === item.menu_item_id ? null : item.menu_item_id)}
                />
              ))}
            </div>
          )}

          {items.length === 0 && result.has_plan && (
            <div className="text-center py-10 text-[#8A7E73]">
              <IndianRupee size={40} className="mx-auto mb-3 opacity-20" />
              <p>Recipes not found for today's menu items. Add recipes in Menu & Recipes first.</p>
            </div>
          )}

          <div className="bg-[#F4F3F0] rounded-xl px-5 py-4 text-xs text-[#8A7E73]">
            <p className="font-bold text-[#5C5147] mb-1">How rates are determined:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>First checks latest <strong>Stock Inward</strong> record for the ingredient (actual purchase price)</li>
              <li>Falls back to active <strong>Vendor Rate Contract</strong> (agreed price)</li>
              <li>If neither exists → shows "No rate" (enter stock inward or vendor contracts to fix)</li>
            </ol>
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="text-center py-16 text-[#8A7E73]">
          <IndianRupee size={48} className="mx-auto mb-3 opacity-20" />
          <p>Select a date and kitchen to calculate recipe costs</p>
        </div>
      )}
    </div>
  );
}
